import { findPileWithCard, type GameState } from '../../engine/game-state.js';
import type { SolitaireRules } from '../../engine/rules.js';
import { fromMap } from '../../render/classic/layout.js';
import type { ClassicPileClickResult, ClassicSlot, ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import {
  drawFromStock,
  hasAnyMove,
  isUncovered,
  PYRAMID_IDS,
  pyramidRules,
} from './rules.js';

const COL_PX = 75;       // ≈ 1.05 card-widths center-to-center
const ROW_PX = 28;       // tighter than TriPeaks (~30% reveal) - 7 rows fit
const HALF_W = CARD_W / 2;
const SELECTED_LIFT = -8; // visual lift in px (negative y = up the page)

const FELT_W = 720;
const TOP_Y = 32;
const STOCK_Y = TOP_Y + 6 * ROW_PX + CARD_H + 28;
const FELT_H = STOCK_Y + CARD_H + 32;
const cx = FELT_W / 2;

function pyramidSlots(): Record<string, ClassicSlot> {
  const out: Record<string, ClassicSlot> = {};
  for (let r = 1; r <= 7; r++) {
    for (let c = 1; c <= r; c++) {
      out[`r${r}c${c}`] = {
        x: cx + (c - (r + 1) / 2) * COL_PX - HALF_W,
        y: TOP_Y + (r - 1) * ROW_PX,
      };
    }
  }
  return out;
}

const slotMap: Record<string, ClassicSlot> = {
  ...pyramidSlots(),
  stock:      { x: cx - 0.6 * COL_PX - HALF_W, y: STOCK_Y },
  waste:      { x: cx + 0.6 * COL_PX - HALF_W, y: STOCK_Y },
  foundation: { x: cx + 2.6 * COL_PX - HALF_W, y: STOCK_Y },
};

const layout = fromMap(slotMap);

// UI-only selection state (not part of GameState).
let selectedId: string | null = null;

function discardCard(state: GameState, fromPileId: string): void {
  const fromPile = state.piles[fromPileId]!;
  const card = fromPile.cards.pop()!;
  state.piles.foundation!.cards.push(card);
}

function applyPair(state: GameState, pileAId: string, pileBId: string): boolean {
  const a = state.piles[pileAId]?.cards.at(-1);
  const b = state.piles[pileBId]?.cards.at(-1);
  if (!a || !b) return false;
  if (a.rank + b.rank !== 13) return false;
  discardCard(state, pileAId);
  discardCard(state, pileBId);
  state.movesCount++;
  return true;
}

function applyKing(state: GameState, fromPileId: string): boolean {
  const card = state.piles[fromPileId]?.cards.at(-1);
  if (!card || card.rank !== 13) return false;
  discardCard(state, fromPileId);
  state.movesCount++;
  return true;
}

function pileOfSelected(state: GameState): string | null {
  if (!selectedId) return null;
  return findPileWithCard(state, selectedId)?.id ?? null;
}

function clickPyramidOrWaste(
  state: GameState,
  pileId: string,
  cardId: string,
): ClassicPileClickResult {
  const pile = state.piles[pileId]!;
  if (pile.type === 'peak' && !isUncovered(state, pileId)) return false;
  const top = pile.cards.at(-1);
  if (!top || top.id !== cardId) return false;

  if (top.rank === 13) {
    if (selectedId === cardId) selectedId = null;
    if (!applyKing(state, pileId)) return false;
    return true;
  }

  if (selectedId && selectedId !== cardId) {
    const otherPileId = pileOfSelected(state);
    if (otherPileId && applyPair(state, otherPileId, pileId)) {
      selectedId = null;
      return true;
    }
  }

  const wasteTop = state.piles.waste!.cards.at(-1);
  if (wasteTop && wasteTop.id !== cardId && wasteTop.rank + top.rank === 13) {
    if (applyPair(state, pileId, 'waste')) {
      selectedId = null;
      return true;
    }
  }

  if (selectedId === cardId) {
    selectedId = null;
    return { commit: false };
  }

  selectedId = cardId;
  return { commit: false };
}

const wrappedRules: SolitaireRules = {
  ...pyramidRules,
  setup(deck) {
    selectedId = null;
    return pyramidRules.setup(deck);
  },
};

export const pyramidClassicSpec: ClassicSpec = {
  rules: wrappedRules,
  feltWidth: FELT_W,
  feltHeight: FELT_H,
  slotForPile: (id) => layout.slot(id),
  dropExclude: () => [...PYRAMID_IDS, 'stock', 'waste', 'foundation'],
  dragStackIds: () => [],
  autoSendOrder: () => ['foundation'],

  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      selectedId = null;
      if (!drawFromStock(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'peak' || pile.type === 'waste') {
      return clickPyramidOrWaste(state, pile.id, cardId);
    }
    return false;
  },

  cardOffset(pile, indexInPile) {
    const card = pile.cards[indexInPile];
    if (card && card.id === selectedId) return { dy: SELECTED_LIFT };
    return {};
  },

  statusText(state) {
    const stockEmpty = state.piles.stock!.cards.length === 0;
    if (stockEmpty && !hasAnyMove(state) && !pyramidRules.isWon(state)) {
      return 'No moves left';
    }
    if (selectedId) {
      const card = findPileWithCard(state, selectedId)?.cards.at(-1);
      if (card) return `${rankLabel(card.rank)} selected - pick a ${rankLabel(13 - card.rank)}`;
    }
    return null;
  },

  cascadeCardIds(state) {
    return state.piles.foundation!.cards.map((c) => c.id);
  },

  showSlotMarker: (id) => id === 'stock' || id === 'waste',
};

function rankLabel(r: number): string {
  if (r === 1) return 'A';
  if (r === 11) return 'J';
  if (r === 12) return 'Q';
  if (r === 13) return 'K';
  return String(r);
}
